import React, { FunctionComponent, useState, useEffect, memo, useMemo } from 'react'
import { AnswerType } from '@models/Survey/Answer'
import { CountryEntry } from '@models/Country'
import { CountryProps } from '../AnswerProps'
import Autocomplete, { KeyValue } from '@components/Autocomplete/Autocomplete'
import countriesEN from './countries-en.json'
import countriesFR from './countries-fr.json'
import countriesES from './countries-es.json'
import Image from '@components/Image/Image'
import quebecImage from '@assets/images/misc/quebec.svg'
import './CountryInput.scss'
import { isUSAApp } from '@models/AppType'
import { useTranslation } from 'react-i18next'
import { flagImages } from '@helpers/import'
import { resolveFlagCdnUrl } from '@helpers/cdnMediaUrl'

type Country = {
  label: string
  value: string
}

const QUEBEC_FLAG_CODE = 'ca-qc'

const flagIconCdnUrl = (flagCode: string) =>
  `https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/4x3/${flagCode}.svg`

const previewFlagSrc = (flagCode: string): string => {
  if (flagCode === QUEBEC_FLAG_CODE) {
    return quebecImage
  }
  // Prefer bundled pixel flags (always available offline); CDN SVG as fallback.
  return flagImages[`${flagCode}.jpg`] ?? flagIconCdnUrl(flagCode)
}

export const CountryInput: FunctionComponent<CountryProps> = ({ question, onSelection, answer }: CountryProps) => {
  const { i18n } = useTranslation()
  const [code, setCode] = useState<string | undefined>((answer as CountryEntry)?.code?.toLowerCase())
  const [term, setTerm] = useState('')

  const countries = useMemo(() => {
    let countriesFromJson = []

    switch (i18n.language) {
      case 'fr':
        countriesFromJson.push(...countriesFR)
        break
      case 'es':
        countriesFromJson.push(...countriesES)
        break
      case 'en':
        countriesFromJson.push(...countriesEN)
        break
      default:
        countriesFromJson.push(...countriesEN)
        break
    }

    countriesFromJson = countriesFromJson.map(({ code: value, name: label }) => ({ value, label } as Country))

    if (isUSAApp) {
      // filter out quebec flag for US elections
      return countriesFromJson.filter((country) => country.value.toLowerCase() !== QUEBEC_FLAG_CODE)
    }
    return countriesFromJson
  }, [i18n.language, countriesFR, countriesEN, countriesES])

  useEffect(() => {
    if (code) {
      const flagCode = code.toLowerCase()
      onSelection({
        code: flagCode,
        artworkUrl: flagCode === QUEBEC_FLAG_CODE ? quebecImage : flagIconCdnUrl(flagCode),
        // Portrait API must fetch Django/CDN media — not Vite `/src/assets/…` URLs.
        pixelatedArtworkUrl: resolveFlagCdnUrl(flagCode),
      })
    }
  }, [code])

  const getDefaultItemFromAnswer = (answer: AnswerType | undefined): KeyValue | undefined => {
    if (!answer) return undefined

    const country = answer as CountryEntry
    const label = countries.find((x) => x.value.toLowerCase() === country.code.toLowerCase())?.label || ''

    return {
      value: country.code,
      label,
    }
  }

  const onChange = (item: KeyValue | undefined) => {
    const selectedCode = item?.value.toLowerCase()
    setCode(selectedCode)
  }

  const filteredCountries = useMemo(() => {
    if (term === '') return

    const normalizedTerm = term
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
    const normalizedCountry = countries.map((country) => ({
      ...country,
      label: country.label
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''),
    }))
    const perfectMatch = normalizedCountry.find((country) => country.label === normalizedTerm)

    if (perfectMatch) {
      setCode(perfectMatch.value.toLowerCase())
    }

    return normalizedCountry
      .filter((country) => country.label.includes(normalizedTerm))
      .sort((a, b) => {
        const aIdx = a.label.indexOf(normalizedTerm)
        const bIdx = b.label.indexOf(normalizedTerm)
        if (aIdx < bIdx) {
          return -1
        }
        if (aIdx === bIdx) {
          return 0
        }
        return 1
      })
  }, [term, countries])

  return (
    <div className="CountryInput">
      <Autocomplete
        placeholder={question.hint || ''}
        items={filteredCountries ?? countries}
        defaultSelectedItem={getDefaultItemFromAnswer(answer)}
        onInputChange={(term: string) => setTerm(term ?? '')}
        onSelectChange={(item) => onChange(item ? item : undefined)}
      />
      {code && (
        <div className="centered-content flag-art">
          <Image src={previewFlagSrc(code.toLowerCase())} />
        </div>
      )}
    </div>
  )
}

export default memo(CountryInput)
